// Assigning an English prerequisite test: pick the diagnostic it is built from, choose which
// sections to assign, see what it would ask, and stage it onto the lesson.
//
// Deliberately a mirror of BmdPrereqTabPanel, down to the copy and the layout. A tutor moves
// between the Mathematics and English tabs of the same page, and a panel that reads differently
// looks like a different feature rather than the same one applied to another subject.
//
// Sections are CLUSTERS, which is what English is assigned by everywhere else - the base test's
// picker ticks clusters too. Maths calls its equivalent a strand; the two are the same rung of
// their respective trees.
//
// Before changing the copy: the count shown is what is owed right now, not the final length. The
// test is adaptive, so a wrong answer earns the next question down the chain while the student is
// taking it. Keep the "more are added" framing.
//
// Nothing is written here. Staging puts a draft on the lesson page, and the page posts it when
// the tutor saves, the same as a base test.

import React, { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import Select, { SingleValue } from "react-select"
import { Input } from "reactstrap"
import { BMD_COLOURS } from "../../bmd/constants/bmd.colours"
import { plainText } from "../../bmd/utils/formatters"
import {
  getEngDiagPrereqCandidates,
  previewEngDiagPrereqAssignment,
  type EngDiagPrereqCandidate,
  type EngDiagPrereqPreview,
} from "../services/eng-diag.service"

/**
 * A prerequisite test waiting to be written, held on the lesson page until the tutor saves.
 *
 * Deliberately the SAME shape as the maths draft (`BmdPrereqStagedDraft`), including the key
 * name `diagnostics_session_key` even though the English endpoint calls that field
 * `parent_session_key`. Both subjects stage into one list, discriminated by `subject_id`, which
 * is how the base diagnostics drafts already work.
 *
 * Two bugs came from having a second, parallel list instead: the lesson page drew a second
 * "Prerequisite Tests" heading, and the Add Activities button - which enables itself from the
 * staged lists by name - could not see an English draft, so staging one on its own left the
 * tutor unable to submit. One list cannot drift from itself.
 */
export interface EngDiagPrereqStagedDraft {
  /** Dedupe key for the staged list. */
  draft_key: string
  /** The base diagnostic this is built from. Sent as `parent_session_key` at submit time. */
  diagnostics_session_key: string
  /** Routes the submit to engDiag rather than bmd. */
  subject_id: number
  student_key: string
  year_level_name: string
  question_count: number
  /** The clusters the tutor ticked. Posted with the draft and pinned on the session. */
  cluster_ids: number[]
  cluster_count: number
  is_homework?: 0 | 1
}

type Props = {
  studentKey: string
  /** Already staged in this visit, so the same diagnostic is not queued twice. */
  stagedKeys?: string[]
  onStageDraft: (draft: EngDiagPrereqStagedDraft) => void
}

// ---------------------------------------------------------------------------
// Design tokens, shared with the maths panel so the two cannot drift apart
// ---------------------------------------------------------------------------

/** Matches ENGLISH_SUBJECT_ID on the lesson page; routes the staged draft to the engDiag API. */
const ENGLISH_SUBJECT_ID = 1

const INK = BMD_COLOURS.INK
const BODY = BMD_COLOURS.BODY
const MUTED = BMD_COLOURS.MUTED
const CARD_BORDER = BMD_COLOURS.CARD_BORDER
const SECTION_BG = BMD_COLOURS.SECTION_BG
const ACCENT = BMD_COLOURS.ACCENT
/** The "how it works" paragraph, which the mockup sets in a readable navy. */
const EXPLAIN = "#35538c"
/** Skill lines inside an open section. */
const SKILL = "#3f6cb5"

const CARD: React.CSSProperties = {
  backgroundColor: "#fff",
  border: `1px solid ${CARD_BORDER}`,
  borderRadius: 10,
  padding: 20,
}

const LABEL: React.CSSProperties = {
  color: INK,
  fontSize: 12.5,
  fontWeight: 700,
  marginBottom: 8,
  display: "block",
}

const HELPER: React.CSSProperties = { color: MUTED, fontSize: 11.5 }

const STAT_LABEL: React.CSSProperties = {
  color: MUTED,
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
}

/**
 * The theme sizes a bare `input[type=checkbox]` to roughly 34px, which would put a large square
 * next to 13px section names. Inline wins over the global rule without a specificity fight.
 */
const CHECKBOX: React.CSSProperties = {
  width: 16,
  height: 16,
  marginTop: 0,
  accentColor: INK,
  verticalAlign: "middle",
  cursor: "pointer",
  flexShrink: 0,
}

type CandidateOption = {
  value: string
  label: string
  candidate: EngDiagPrereqCandidate
}

/** "31 Aug 2026". Fixed rather than locale-default, which gives "8/31/2026". */
function formatDate(iso: string | null): string {
  if (!iso) return ""
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })
}

function candidateLabel(c: EngDiagPrereqCandidate): string {
  const year = c.level_name || "Diagnostic"
  const when = formatDate(c.completed_on || c.created_on)
  // "incorrect", matching the helper line underneath. The tutor is picking the diagnostic by how
  // much went wrong in it, so that is the number in the label.
  const wrong = `${c.wrong_answer_count} of ${c.base_question_count} incorrect`
  return `${year}${when ? ` · ${when}` : ""} · ${wrong}`
}

/** One column of the stat strip. */
const Stat: React.FC<{
  label: string
  value: React.ReactNode
  unit: string
  note?: string
}> = ({ label, value, unit, note }) => (
  <div style={{ minWidth: 130 }}>
    <div style={STAT_LABEL}>{label}</div>
    <div className="d-flex align-items-baseline gap-2 mt-1">
      <span style={{ color: INK, fontSize: 24, fontWeight: 700, lineHeight: 1 }}>
        {value}
      </span>
      <span style={{ color: BODY, fontSize: 12, maxWidth: 78, lineHeight: 1.2 }}>
        {unit}
      </span>
    </div>
    {note ? (
      <div style={{ color: MUTED, fontSize: 11, marginTop: 6, maxWidth: 150 }}>
        {note}
      </div>
    ) : null}
  </div>
)

export function EngDiagPrereqTabPanel({
  studentKey,
  stagedKeys = [],
  onStageDraft,
}: Props) {
  const [candidates, setCandidates] = useState<EngDiagPrereqCandidate[]>([])
  const [loadingCandidates, setLoadingCandidates] = useState(false)
  const [candidatesError, setCandidatesError] = useState<string | null>(null)
  const [selected, setSelected] = useState<SingleValue<CandidateOption>>(null)
  const [preview, setPreview] = useState<EngDiagPrereqPreview | null>(null)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)

  useEffect(() => {
    if (!studentKey) return undefined
    const controller = new AbortController()
    setLoadingCandidates(true)
    setCandidatesError(null)
    getEngDiagPrereqCandidates({ student_key: studentKey }, controller.signal)
      .then(data => {
        if (controller.signal.aborted) return
        setCandidates(data.candidates || [])
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setCandidatesError("Could not load this student's diagnostics.")
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingCandidates(false)
      })
    return () => controller.abort()
  }, [studentKey])

  const selectedKey = selected?.candidate.pk_session_key || ""

  useEffect(() => {
    if (!selectedKey) {
      setPreview(null)
      setPreviewError(null)
      return undefined
    }
    const controller = new AbortController()
    setLoadingPreview(true)
    setPreviewError(null)
    previewEngDiagPrereqAssignment(
      { parent_session_key: selectedKey, student_key: studentKey },
      controller.signal,
    )
      .then(data => {
        if (!controller.signal.aborted) setPreview(data)
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setPreviewError("Could not work out what this test would ask.")
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingPreview(false)
      })
    return () => controller.abort()
  }, [selectedKey, studentKey])

  const options: CandidateOption[] = useMemo(
    () =>
      candidates
        // A diagnostic with nothing wrong has no chains to follow.
        .filter(c => c.wrong_answer_count > 0)
        .map(c => ({
          value: c.pk_session_key,
          label: candidateLabel(c),
          candidate: c,
        })),
    [candidates],
  )

  const alreadyStaged = selectedKey ? stagedKeys.includes(selectedKey) : false
  const alreadyAssigned = !!selected?.candidate.already_assigned
  const nothingOwed = !!preview && preview.question_count === 0

  // Which sections are ticked. Everything starts ticked, matching the behaviour from before the
  // picker existed.
  const [tickedIds, setTickedIds] = useState<number[]>([])
  // Which sections are open. The first opens by default, so the affordance is visible without the
  // tutor having to discover it.
  const [openIds, setOpenIds] = useState<number[]>([])
  useEffect(() => {
    const clusters = preview?.clusters || []
    setTickedIds(clusters.map(c => c.cluster_id))
    setOpenIds(clusters.length > 0 ? [clusters[0].cluster_id] : [])
  }, [preview])

  // Sections this test is already committed to cannot be un-ticked: their questions are written
  // and may already have been answered.
  const lockedIds = useMemo(
    () => new Set(preview?.selected_cluster_ids || []),
    [preview],
  )

  const toggle = (id: number) => {
    if (lockedIds.has(id)) return
    setTickedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    )
  }

  const toggleOpen = (id: number) =>
    setOpenIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    )

  const tickedSet = useMemo(() => new Set(tickedIds), [tickedIds])
  const openSet = useMemo(() => new Set(openIds), [openIds])
  const tickedClusters = useMemo(
    () => (preview?.clusters || []).filter(c => tickedSet.has(c.cluster_id)),
    [preview, tickedSet],
  )
  // The count that matters is the ticked one, not the whole diagnostic.
  const selectedQuestionCount = tickedClusters.reduce(
    (n, c) => n + (c.question_count || 0),
    0,
  )
  const noneTicked = !!preview && !nothingOwed && tickedIds.length === 0
  const allTicked =
    !!preview && tickedIds.length === (preview.clusters || []).length

  const canAdd = !!preview && !nothingOwed && !alreadyStaged && !noneTicked

  const handleStage = () => {
    if (!selected || !preview || !canAdd) return
    onStageDraft({
      draft_key: `prereq-${selectedKey}`,
      diagnostics_session_key: selectedKey,
      subject_id: ENGLISH_SUBJECT_ID,
      student_key: studentKey,
      year_level_name: selected.candidate.level_name,
      question_count: selectedQuestionCount,
      cluster_ids: tickedIds,
      cluster_count: tickedClusters.length,
    })
    // Prerequisite drafts land in their OWN panel on the right, "Prerequisite Tests", not under
    // Assigned Diagnostics - naming the wrong panel would send the tutor looking in the wrong
    // place for something that did save.
    toast.success("Added to Prerequisite Tests", { autoClose: 2000 })
    setSelected(null)
  }

  return (
    <div className="p-2">
      {/* ------------------------------------------------------------------ */}
      {/* Which diagnostic to build from                                      */}
      {/* ------------------------------------------------------------------ */}
      <div style={CARD}>
        <label style={LABEL} htmlFor="eng-prereq-source">
          Base this on a completed diagnostic
        </label>
        <div style={{ maxWidth: 420 }}>
          <Select
            inputId="eng-prereq-source"
            value={selected}
            onChange={setSelected}
            options={options}
            isLoading={loadingCandidates}
            isClearable
            placeholder={
              loadingCandidates
                ? "Loading diagnostics…"
                : options.length === 0
                  ? "No diagnostics with incorrect answers"
                  : "Select a completed diagnostic"
            }
            classNamePrefix="select2-selection"
          />
        </div>
        <div style={{ ...HELPER, marginTop: 8 }}>
          Only diagnostics with at least one incorrect answer can be used.
        </div>
      </div>

      {candidatesError ? (
        <div className="text-danger font-size-13 mt-3">{candidatesError}</div>
      ) : null}

      {/* ------------------------------------------------------------------ */}
      {/* Already has a prerequisite test                                     */}
      {/* ------------------------------------------------------------------ */}
      {selected && alreadyAssigned ? (
        <div
          className="d-flex align-items-start gap-3 mt-3"
          style={{
            backgroundColor: "#eef3fa",
            border: "1px solid #dbe6f5",
            borderRadius: 8,
            padding: "14px 16px",
          }}
        >
          <i
            className="mdi mdi-information-outline"
            style={{ color: "#5a7fb8", fontSize: 16, lineHeight: 1.4 }}
            aria-hidden="true"
          ></i>
          <div>
            <div style={{ color: INK, fontSize: 13, fontWeight: 700 }}>
              This diagnostic already has a prerequisite test
            </div>
            {/* "hasn't attempted", not "hasn't seen": a question the student skipped HAS been
                seen, and it is still owed, so "seen" described the wrong set. */}
            <div style={{ color: BODY, fontSize: 12.5, marginTop: 2 }}>
              Assigning again adds only questions the student hasn't attempted. Nothing already
              asked is repeated.
            </div>
          </div>
        </div>
      ) : null}

      {selected && loadingPreview ? (
        <div className="text-muted font-size-13 mt-3">Loading…</div>
      ) : null}

      {previewError ? (
        <div className="text-danger font-size-13 mt-3">{previewError}</div>
      ) : null}

      {/* ------------------------------------------------------------------ */}
      {/* The test itself                                                     */}
      {/* ------------------------------------------------------------------ */}
      {selected && preview && !loadingPreview ? (
        <div style={{ ...CARD, marginTop: 16 }}>
          <div className="d-flex flex-wrap align-items-start gap-4">
            <Stat
              label="Assigns now"
              value={selectedQuestionCount}
              unit={nothingOwed ? "new questions" : "questions"}
              note={
                nothingOwed
                  ? "Nothing left to add for now."
                  : "More are added as the student answers."
              }
            />
            <div
              style={{ width: 1, alignSelf: "stretch", backgroundColor: CARD_BORDER }}
              aria-hidden="true"
            ></div>
            <div style={{ flex: "1 1 340px", minWidth: 260 }}>
              <div style={STAT_LABEL}>How it works</div>
              <div
                style={{
                  color: EXPLAIN,
                  fontSize: 12.5,
                  lineHeight: 1.55,
                  marginTop: 6,
                }}
              >
                Each incorrect answer opens a chain. Answer the prerequisite skill correctly and
                the chain stops there. Answer it wrongly and the next skill down is added,
                continuing year by year, as far as Reception if needed.
              </div>
            </div>
          </div>

          {nothingOwed ? (
            <div
              className="d-flex align-items-start gap-3 mt-4"
              style={{
                backgroundColor: "#fdf6e7",
                border: "1px solid #f0e0bd",
                borderRadius: 8,
                padding: "14px 16px",
              }}
            >
              <i
                className="mdi mdi-alert-outline"
                style={{ color: "#b0862f", fontSize: 16, lineHeight: 1.4 }}
                aria-hidden="true"
              ></i>
              <div>
                <div style={{ color: INK, fontSize: 13, fontWeight: 700 }}>
                  Nothing to assign from this diagnostic
                </div>
                <div style={{ color: BODY, fontSize: 12.5, marginTop: 2 }}>
                  Every available prerequisite question has already been asked.
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="d-flex align-items-center flex-wrap gap-2 mt-4 mb-2">
                <span style={{ color: INK, fontSize: 13, fontWeight: 700 }}>
                  Which sections to assign
                </span>
                <span
                  style={{
                    backgroundColor: SECTION_BG,
                    color: BODY,
                    borderRadius: 20,
                    padding: "3px 10px",
                    fontSize: 11,
                  }}
                >
                  {tickedClusters.length} section
                  {tickedClusters.length === 1 ? "" : "s"} ·{" "}
                  {selectedQuestionCount} question
                  {selectedQuestionCount === 1 ? "" : "s"}
                </span>
                <button
                  type="button"
                  className="btn btn-link p-0 ms-auto"
                  style={{ color: ACCENT, fontSize: 12, textDecoration: "none" }}
                  onClick={() =>
                    setTickedIds(
                      allTicked
                        ? // Locked sections stay: their questions are already written and
                          // cannot be un-asked.
                          preview.clusters
                            .map(c => c.cluster_id)
                            .filter(id => lockedIds.has(id))
                        : preview.clusters.map(c => c.cluster_id),
                    )
                  }
                >
                  {allTicked ? "Clear all" : "Select all"}
                </button>
              </div>

              {preview.clusters.map(cluster => {
                const id = cluster.cluster_id
                const locked = lockedIds.has(id)
                const isOpen = openSet.has(id)
                const hasSkills = cluster.skills.length > 0
                return (
                  <div
                    key={id}
                    style={{
                      border: `1px solid ${CARD_BORDER}`,
                      borderRadius: 8,
                      marginBottom: 10,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      className="d-flex align-items-center gap-3"
                      style={{
                        backgroundColor: SECTION_BG,
                        padding: "12px 14px",
                        cursor: hasSkills ? "pointer" : "default",
                      }}
                      onClick={() => {
                        if (hasSkills) toggleOpen(id)
                      }}
                    >
                      <Input
                        type="checkbox"
                        id={`eng-prereq-cluster-${id}`}
                        checked={tickedSet.has(id)}
                        disabled={locked}
                        onChange={() => toggle(id)}
                        // The header toggles the accordion; the checkbox must only tick, or a
                        // tutor aiming for the box also opens the section under their cursor.
                        onClick={e => e.stopPropagation()}
                        style={CHECKBOX}
                        className="m-0"
                      />
                      <span
                        style={{
                          color: INK,
                          fontSize: 13,
                          fontWeight: 700,
                          flexGrow: 1,
                          minWidth: 0,
                        }}
                      >
                        {plainText(cluster.display_name)}
                      </span>
                      {locked ? (
                        <span style={{ color: MUTED, fontSize: 11.5 }}>
                          already assigned
                        </span>
                      ) : null}
                      <span style={{ color: MUTED, fontSize: 12 }}>
                        {cluster.question_count} question
                        {cluster.question_count === 1 ? "" : "s"}
                      </span>
                      {hasSkills ? (
                        <i
                          className={`mdi mdi-chevron-${isOpen ? "up" : "down"}`}
                          style={{ color: MUTED, fontSize: 18 }}
                          aria-hidden="true"
                        ></i>
                      ) : null}
                    </div>

                    {isOpen && hasSkills ? (
                      // Two columns. Skill names are short enough to pair up and a long single
                      // column pushed the next section off the screen.
                      <div
                        style={{
                          padding: "12px 14px 14px 44px",
                          columnCount: 2,
                          columnGap: 32,
                        }}
                      >
                        {cluster.skills.map(s => (
                          <div
                            key={s.skill_id}
                            style={{
                              color: SKILL,
                              fontSize: 12,
                              lineHeight: 1.9,
                              breakInside: "avoid",
                            }}
                          >
                            {plainText(s.skill_name)}
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                )
              })}

              {noneTicked ? (
                <div className="text-danger font-size-12 mt-1">
                  Select at least one section.
                </div>
              ) : null}
              {!allTicked && !noneTicked ? (
                <div style={{ ...HELPER, marginTop: 4 }}>
                  Unticked sections are not assigned, even when a ticked section leads into them.
                </div>
              ) : null}

              {/* A skill a chain wants but cannot ask about. Worth showing rather than hiding:
                  it is a gap in the question bank, and only the curriculum team can close it. */}
              {preview.skipped_no_question.length > 0 ? (
                <div style={{ ...HELPER, marginTop: 8 }}>
                  {preview.skipped_no_question.length} skill
                  {preview.skipped_no_question.length === 1 ? " has" : "s have"} no question
                  written yet and cannot be asked:{" "}
                  {preview.skipped_no_question
                    .map(s => plainText(s.skill_name) || s.skill_id)
                    .join(", ")}
                </div>
              ) : null}
            </>
          )}

          <hr style={{ borderColor: CARD_BORDER, margin: "20px 0 16px" }} />

          <div className="d-flex align-items-center flex-wrap gap-3">
            <button
              type="button"
              className="btn fw-bold"
              style={{
                backgroundColor: canAdd ? ACCENT : "#e9ecf1",
                borderColor: canAdd ? ACCENT : "#e9ecf1",
                color: canAdd ? "#fff" : MUTED,
                borderRadius: 6,
                padding: "9px 20px",
                fontSize: 13,
              }}
              disabled={!canAdd}
              onClick={handleStage}
            >
              {alreadyStaged ? "Already added" : "Add to assignment"}
            </button>
            <span style={HELPER}>
              {nothingOwed
                ? "Available once there are new questions to add."
                : "Confirmed when you press Add Activities."}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  )
}
